import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { Loader2, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { AddFundsModal } from '@/features/settings/components/add-funds-modal';
import { apiClient } from '@/lib/api-client';
import { useFeatureFlag } from '@/lib/use-feature-flag';
import { addGeneratingIdAtom, removeGeneratingIdAtom } from '@/stores/generating.store';
import { setSelectedChecksAtom, wizardStateAtom } from '@/stores/wizard.store';
import {
  getScreeningStatus,
  useClientDetails,
  useScreeningStatus,
  wizardKeys,
} from '../api/risk-assessment-wizard';
import { ALL_SCREENING_CHECKS, getDefaultSelectedLists } from '../constants/sanctions-lists';
import { usePaymentFlow } from '../hooks/use-payment-flow';

import { DownloadReportModal } from './download-report-modal';
import { EddModal } from './edd-modal';
import { EddUpsellModal } from './edd-upsell-modal';
import { ListSelectionFooter } from './list-selection-footer';
import { MatchReportModal } from './match-report-modal';
import { NoReportAccessModal } from './no-report-access-modal';
import { PayForSearchModal } from './pay-for-search-modal';
import { RequestDocsModal } from './request-docs-modal';
import { ScreeningModal } from './screening-modal';
import { ScreeningProgressModal } from './screening-progress-modal';
import { WizardLayout } from './wizard-layout';

interface ListSelectionFormProps {
  assessmentId: string;
}

export function ListSelectionForm({ assessmentId }: ListSelectionFormProps) {
  const navigate = useNavigate();
  const { data: clientDetails, isLoading: isLoadingClient } = useClientDetails(assessmentId);
  const addGeneratingId = useSetAtom(addGeneratingIdAtom);
  const removeGeneratingId = useSetAtom(removeGeneratingIdAtom);
  const setSelectedChecksStore = useSetAtom(setSelectedChecksAtom);
  const wizardState = useAtomValue(wizardStateAtom);
  const { enabled: documentUploadEnabled } = useFeatureFlag('document_upload');

  const [searchQuery, setSearchQuery] = useState('');
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Initialize from wizard store if available, otherwise use defaults
  const [selectedChecks, setSelectedChecks] = useState<string[]>(() =>
    wizardState.selectedChecks.length > 0 ? wizardState.selectedChecks : getDefaultSelectedLists(),
  );

  // Track whether we've seen PROCESSING status since entering REPORT_GENERATING
  // This prevents stale COMPLETED status from triggering immediate navigation after EDD purchase
  const hasSeenProcessingRef = useRef(false);

  // Payment flow management - only used for checking IDLE state for redirect
  const {
    state: paymentState,
    handlePayForSearch,
    handleScreeningComplete,
    proceedFromDownloadReport,
    handleDocRequest,
    skipDocRequest,
    handleMatchReportPurchase,
    skipMatchReport,
    skipEddUpsell,
    handleEddPurchase,
    skipEdd,
    goBackToPurchase,
    completeFlow,
    resetFlow,
    isScreeningPending,
  } = usePaymentFlow(assessmentId, { documentUploadEnabled });

  // Only enable polling during active screening flows (not IDLE)
  const shouldPollStatus =
    paymentState.step === 'SCREENING_PROGRESS' || paymentState.step === 'REPORT_GENERATING';

  // Poll screening status during active flows
  const { data: polledScreeningStatus } = useScreeningStatus(assessmentId, shouldPollStatus);

  // Fetch once on mount for IDLE state (staleTime handles caching)
  const { data: initialStatus } = useQuery({
    queryKey: wizardKeys.screeningStatus(assessmentId),
    queryFn: () => getScreeningStatus(assessmentId),
    enabled: paymentState.step === 'IDLE' && !!assessmentId,
    staleTime: 60000, // Cache for 1 minute - no need to refetch frequently in IDLE
  });

  // Use whichever status is available (polled during active flow, initial in IDLE)
  const screeningStatus = polledScreeningStatus ?? initialStatus;

  // Redirect users who have already paid for screening to depth-selection page
  // The depth-selection page handles resuming the payment flow and showing appropriate modals
  useEffect(() => {
    if (screeningStatus && paymentState.step === 'IDLE') {
      const paidForScreening = screeningStatus.paidServices?.initialScreening;
      const screeningStartedOrCompleted =
        screeningStatus.status === 'PROCESSING' || screeningStatus.status === 'COMPLETED';

      // If user has paid for screening, redirect to depth-selection which will show the appropriate modal
      if (paidForScreening || screeningStartedOrCompleted) {
        navigate({ to: '/risk-assessment/$id/depth-selection', params: { id: assessmentId } });
      }
    }
  }, [screeningStatus, paymentState.step, navigate, assessmentId]);

  // Reset the "seen processing" flag when we're not in REPORT_GENERATING
  useEffect(() => {
    if (paymentState.step !== 'REPORT_GENERATING') {
      hasSeenProcessingRef.current = false;
    }
  }, [paymentState.step]);

  // Handle screening status changes
  useEffect(() => {
    if (!screeningStatus) return;

    // Don't advance if screening mutation is still pending (prevents stale cache from triggering advance)
    if (isScreeningPending) return;

    // When screening completes during SCREENING_PROGRESS, move to next step
    if (paymentState.step === 'SCREENING_PROGRESS' && screeningStatus.status === 'COMPLETED') {
      const riskLevel = screeningStatus.riskLevel || 'LOW';
      handleScreeningComplete(riskLevel);
    }

    // Track when we see PROCESSING status during REPORT_GENERATING (EDD upgrade in progress)
    if (paymentState.step === 'REPORT_GENERATING' && screeningStatus.status === 'PROCESSING') {
      hasSeenProcessingRef.current = true;
    }

    // When screening completes during REPORT_GENERATING (EDD upgrade), navigate to report
    // Must have seen PROCESSING status first to avoid stale COMPLETED triggering immediate navigation
    const isEddUpgrade = paymentState.purchasedServices.eddReport;
    const canComplete = isEddUpgrade ? hasSeenProcessingRef.current : true;

    if (
      paymentState.step === 'REPORT_GENERATING' &&
      screeningStatus.status === 'COMPLETED' &&
      canComplete
    ) {
      // Send report ready email now that EDD processing is complete
      if (isEddUpgrade) {
        apiClient
          .fetch(`/risk-assessments/${assessmentId}/send-report-email`, { method: 'POST' })
          .catch((err) => console.warn('Failed to send report ready email:', err));
      }

      removeGeneratingId(assessmentId);
      completeFlow();
    }
  }, [
    screeningStatus?.status,
    paymentState.step,
    paymentState.purchasedServices.eddReport,
    assessmentId,
    handleScreeningComplete,
    completeFlow,
    removeGeneratingId,
    screeningStatus,
    isScreeningPending,
  ]);

  // Navigate to report when flow completes
  useEffect(() => {
    if (paymentState.step === 'COMPLETE') {
      navigate({ to: '/risk-assessment/$id/report', params: { id: assessmentId } });
    }
  }, [paymentState.step, assessmentId, navigate]);

  // Filter all checks based on search query (matches name or ID)
  const filteredChecks = useMemo(() => {
    if (!searchQuery.trim()) {
      return ALL_SCREENING_CHECKS;
    }
    const query = searchQuery.toLowerCase();
    return ALL_SCREENING_CHECKS.filter(
      (check) => check.name.toLowerCase().includes(query) || check.id.toLowerCase().includes(query),
    );
  }, [searchQuery]);

  const handleToggleCheck = (checkId: string) => {
    setValidationError(null); // Clear error when user makes a selection
    setSelectedChecks((prev) =>
      prev.includes(checkId) ? prev.filter((id) => id !== checkId) : [...prev, checkId],
    );
  };

  const handleSelectAll = () => {
    setSelectedChecks(ALL_SCREENING_CHECKS.map((check) => check.id));
  };

  const handleDeselectAll = () => {
    setSelectedChecks([]);
  };

  // Build client name for display
  const clientName = useMemo(() => {
    if (!clientDetails) return '';
    const parts = [clientDetails.forename, clientDetails.middleName, clientDetails.surname].filter(
      Boolean,
    );
    return parts.join(' ');
  }, [clientDetails]);

  // Get client email for document request (uses empty string as placeholder since email may not be in client details)
  const clientEmail = '';

  const handleSaveDraft = () => {
    // For now, just show a toast or do nothing since we're not persisting to DB
    // In the future, this could save the selected lists to the assessment
  };

  const handleCancel = () => {
    navigate({ to: '/' });
  };

  // Navigate to depth selection when user clicks "Carry Out Check"
  const handleCarryOutCheck = () => {
    if (selectedChecks.length === 0) {
      setValidationError('Please select at least one check to run an assessment.');
      return;
    }
    setValidationError(null);
    // Store selected checks in wizard state for the next step
    setSelectedChecksStore(selectedChecks);
    // Navigate to depth selection page
    navigate({ to: '/risk-assessment/$id/depth-selection', params: { id: assessmentId } });
  };

  // Handle payment confirmation for initial screening
  const handlePaymentConfirmed = async () => {
    addGeneratingId(assessmentId);
    const success = await handlePayForSearch(selectedChecks);
    if (!success) {
      removeGeneratingId(assessmentId);
    }
  };

  // Handle add funds request - opens the add funds modal
  const handleAddFunds = () => {
    setShowAddFundsModal(true);
  };

  const handleBackToDashboard = () => {
    resetFlow();
    navigate({ to: '/' });
  };

  // Handle view report from screening modal (for report generation after EDD)
  const handleViewReport = () => {
    completeFlow();
  };

  if (isLoadingClient) {
    return (
      <WizardLayout currentStep={2} completedSteps={[1]}>
        <div className="flex h-full items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </WizardLayout>
    );
  }

  return (
    <WizardLayout currentStep={2} completedSteps={[1]}>
      <div className="space-y-8 px-8 pb-8">
        {/* Header Section */}
        <section>
          <h1 className="mb-4 text-[24px] font-semibold text-[#051923]">Begin AML Screening</h1>
          <p className="mb-4 text-[16px] text-[#051923]">
            Select the lists you would like to perform a high level search for the client,{' '}
            {clientName || 'the client'}.
          </p>
          <p className="text-[14px] leading-relaxed text-[#6B7280]">
            In addition to screening for PEP &amp; Sanctions we will also carry out a high level
            AI-driven web search for adverse media in relation to the subject, Companies House
            affirmation of Directorships and assess risk using the Corruption Perceptions Index.
            Where relevant, we will highlight any additional risks this raises for further review.
          </p>
        </section>

        {/* List Selector Box */}
        <section className="rounded-lg border border-[#E5E7EB] p-6">
          {/* Search and Bulk Actions */}
          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search lists"
                aria-label="Search screening lists"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="link"
                onClick={handleSelectAll}
                className="h-auto p-0 text-[14px] font-medium text-[#051923] underline hover:text-[#374151]"
              >
                Select All
              </Button>
              <Button
                type="button"
                variant="link"
                onClick={handleDeselectAll}
                className="h-auto p-0 text-[14px] font-medium text-[#051923] underline hover:text-[#374151]"
              >
                Deselect All
              </Button>
            </div>
          </div>

          {/* Checkbox List */}
          <div className="space-y-4">
            {filteredChecks.map((check) => {
              const isSelected = selectedChecks.includes(check.id);
              return (
                <div key={check.id} className="flex items-center gap-3">
                  <Checkbox
                    id={`check-${check.id}`}
                    checked={isSelected}
                    onCheckedChange={() => handleToggleCheck(check.id)}
                  />
                  <label
                    htmlFor={`check-${check.id}`}
                    className="cursor-pointer text-[14px] text-[#051923]"
                  >
                    {check.name}
                  </label>
                </div>
              );
            })}
          </div>

          {filteredChecks.length === 0 && (
            <div className="py-4 text-center">
              <p className="text-[14px] text-gray-500">
                No lists match your search for "{searchQuery}".
              </p>
              <Button
                type="button"
                variant="link"
                onClick={() => setSearchQuery('')}
                className="mt-2 h-auto p-0 text-[14px] font-medium text-[#051923] underline"
              >
                Clear search
              </Button>
            </div>
          )}

          {/* Validation Error */}
          {validationError && (
            <div className="mt-4 rounded-md bg-red-50 p-3">
              <p className="text-[14px] text-red-600">{validationError}</p>
            </div>
          )}
        </section>
      </div>

      <ListSelectionFooter
        onSaveDraft={handleSaveDraft}
        onCancel={handleCancel}
        onCarryOutCheck={handleCarryOutCheck}
        isSaving={isScreeningPending || paymentState.isProcessing}
      />

      {/* Payment Flow Modals */}

      {/* Step 1: Pay for Search */}
      <PayForSearchModal
        open={paymentState.step === 'PAY_FOR_SEARCH'}
        onOpenChange={(open) => !open && resetFlow()}
        onPurchase={handlePaymentConfirmed}
        onAddFunds={handleAddFunds}
        isPurchasing={paymentState.isProcessing}
      />

      {/* Step 2: Screening Progress */}
      <ScreeningProgressModal
        open={paymentState.step === 'SCREENING_PROGRESS'}
        screeningStatus={screeningStatus}
        isInitialScreening={isScreeningPending}
        onBackToDashboard={handleBackToDashboard}
        onComplete={() => {
          if (screeningStatus?.riskLevel) {
            handleScreeningComplete(screeningStatus.riskLevel);
          }
        }}
      />

      {/* Step 2.5: Download Report (after screening completes, before purchase modals) */}
      <DownloadReportModal
        open={paymentState.step === 'DOWNLOAD_REPORT'}
        assessmentId={assessmentId}
        onSkip={proceedFromDownloadReport}
      />

      {/* Step 3: Match Report (for ALL users regardless of risk level) */}
      <MatchReportModal
        open={paymentState.step === 'MATCH_REPORT'}
        riskLevel={paymentState.riskLevel}
        onPurchase={handleMatchReportPurchase}
        onSkip={skipMatchReport}
        onAddFunds={handleAddFunds}
        isPurchasing={paymentState.isProcessing}
      />

      {/* Step 3b: EDD Upsell (shown after Match Report purchase) */}
      <EddUpsellModal
        open={paymentState.step === 'EDD_UPSELL'}
        onPurchase={handleEddPurchase}
        onSkip={skipEddUpsell}
        onAddFunds={handleAddFunds}
        isPurchasing={paymentState.isProcessing}
      />

      {/* Step 3c: Enhanced Due Diligence (for direct EDD purchase) */}
      <EddModal
        open={paymentState.step === 'EDD'}
        onPurchase={handleEddPurchase}
        onSkip={skipEdd}
        onAddFunds={handleAddFunds}
        isPurchasing={paymentState.isProcessing}
      />

      {/* Step 4: Request Documentation (after skipping report) - Step 2/2 */}
      <RequestDocsModal
        open={paymentState.step === 'REQUEST_DOCS'}
        clientEmail={clientEmail}
        onPurchase={(email, documentTypes, otherDescription) =>
          handleDocRequest(email, documentTypes, otherDescription)
        }
        onSkip={skipDocRequest}
        onAddFunds={handleAddFunds}
        isPurchasing={paymentState.isProcessing}
      />

      {/* Step 5a: Report Generating (after purchasing report) */}
      <ScreeningModal
        open={paymentState.step === 'REPORT_GENERATING'}
        onOpenChange={() => undefined}
        isScreening={false}
        screeningStatus={screeningStatus}
        onViewReport={handleViewReport}
        onBackToDashboard={handleBackToDashboard}
      />

      {/* Step 5b: No Report Access (skipped report and docs - return to dashboard) */}
      <NoReportAccessModal
        open={paymentState.step === 'NO_ACCESS'}
        onOpenChange={() => undefined}
        onGoBack={goBackToPurchase}
        onReturnToDashboard={handleBackToDashboard}
      />

      {/* Add Funds Modal */}
      <AddFundsModal open={showAddFundsModal} onOpenChange={setShowAddFundsModal} />
    </WizardLayout>
  );
}
